import React from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import { EntityAssignment } from '../../types/workflow';

interface EntityValidationWarningProps {
  /** The entity assignment to check */
  entity: EntityAssignment | null | undefined;
  /** The role label (e.g. "Client", "Lab") shown in the warning message */
  role: string;
  /** Which validation to perform */
  variant: 'sage-readiness' | 'email-missing';
  /** Optional callback to fix the issue — shows "Fix" button when provided */
  onFix?: () => void;
  /** Whether the fix action is loading */
  fixLoading?: boolean;
}

/**
 * EntityValidationWarning Component
 *
 * Generic validation warning for entity assignments with two variants:
 * - sage-readiness: warns when entity lacks Sage accounting details
 * - email-missing: warns when entity lacks an email address
 *
 * Optionally renders a "Fix" button that opens the entity's edit dialog.
 */
export const EntityValidationWarning: React.FC<EntityValidationWarningProps> = ({
  entity,
  role,
  variant,
  onFix,
  fixLoading,
}) => {
  if (!entity) return null;

  if (variant === 'sage-readiness' && entity.isSageReady) return null;
  if (variant === 'email-missing' && entity.email) return null;

  const message =
    variant === 'sage-readiness'
      ? `The selected ${role} does not have Sage accounting details configured. Please update their Sage Name before generating invoices or purchase orders.`
      : `The selected ${role} does not have an email address configured. An email is required for sending recommendations and reports.`;

  return (
    <Alert
      severity="warning"
      sx={{ mt: 1 }}
      action={
        onFix ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onFix}
            disabled={fixLoading}
            startIcon={fixLoading ? <CircularProgress size={14} /> : undefined}
            sx={{
              borderColor: 'warning.dark',
              color: 'warning.dark',
              '&:hover': {
                borderColor: 'warning.main',
                bgcolor: 'warning.light',
              },
            }}
          >
            {fixLoading ? 'Loading...' : 'Fix'}
          </Button>
        ) : undefined
      }
    >
      {message}
    </Alert>
  );
};

export default EntityValidationWarning;
